import {Component, ElementRef, HostListener, Input, OnInit, inject, signal, OnDestroy} from '@angular/core';
import {MatSidenav} from '@angular/material/sidenav';

import {TranslateService} from '@ngx-translate/core';

import {UserService} from '../../../user/services/user.service';
import {MatSelectChange} from "@angular/material/select";
import {ApiService} from "../../../shared/services/api.service";
import {UserModel} from "../../../../models/user.model";
import {ROLE_OPTIONS, UserRole} from "../../../../enums/user-role.enum";
import {UsersService} from "../../../shared/services/users.service";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {LocaleService} from "../../../shared/services/locale.service";
import {endpoints} from "../../../../../environments/endpoints";
import {SessionService, SessionState} from "../../../auth/services/session.service";
import {environment} from "../../../../../environments/environment";

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    @Input() public nav?: MatSidenav;
    userService = inject(UserService);
    usersService = inject(UsersService);
    apiService = inject(ApiService);
    translate = inject(TranslateService);
    sessionService = inject(SessionService);
    private _eRef = inject(ElementRef);
    user = signal<UserModel | null>(null);
    isLoggedIn = signal(false);
    isDropdownOpen = signal(false);
    users = signal<UserModel[]>([]);
    canChangeUser = signal(!environment.production);
    protected readonly ROLE_OPTIONS = ROLE_OPTIONS;

    constructor(
        private localeService: LocaleService
    ) { }

    ngOnInit(): void {
        this.translate.addLangs(['en', 'fr']);

        // Set a default language if there's no language set
        if (!this.translate.currentLang) {
            const browserLang = this.translate.getBrowserLang();
            this.translate.use(browserLang && this.translate.getLangs().includes(browserLang) ? browserLang : 'fr');
        }

        // Subscribe to user changes to keep the signals updated
        this.userService.user$.pipe(takeUntil(this.destroy$)).subscribe(user => {
            this.user.set(user);
            this.isLoggedIn.set(user !== null);
        });

        // Subscribe to session state changes to reload users when authenticated
        this.sessionService.sessionState$.pipe(takeUntil(this.destroy$)).subscribe(state => {
            if (state === SessionState.AUTHENTICATED) {
                this.loadAllUsers();
            }
        });

        this.loadAllUsers();
    }

    getCurrentLangLabel(): string {
        return this.getLangLabel(this.translate.currentLang);
    }

    getLangLabel(lang: string): string {
        const labels: Record<string, string> = {
            fr: 'Français',
            en: 'English',
        };
        return labels[lang] || '';
    }

    toggleDropdown() {
        this.isDropdownOpen.set(!this.isDropdownOpen());
    }

    switchLanguage(language: string) {
        this.translate.use(language);

        const userId = this.user()?.id;
        if (userId !== undefined) {
            this.usersService.update(userId, {lang: language}).pipe(takeUntil(this.destroy$)).subscribe({
                error: (error: any) => {
                    console.error(error);
                },
            });
        }
    }

    getUserDisplayName() {
        return this.user()?.firstName + ' ' + this.user()?.lastName.substring(0, 1) + '.';
    }

    @HostListener('document:click', ['$event'])
    handleClickOutside(event: Event) {
        if (!this._eRef.nativeElement.contains(event.target)) {
            this.isDropdownOpen.set(false);
        }
    }

    loadAllUsers() {
        if (!environment.production && this.userService.isLoggedIn()) {
            this.apiService.get("users/getallusers").pipe(takeUntil(this.destroy$)).subscribe({
                next: (data: any) => {
                    this.users.set(data.data);
                },
                error: (error: any) => {
                    console.error(error);
                },
            });
        }
    }

    changeUser(user: MatSelectChange) {
        this.apiService.post<{ redirectUrl: string }, { igg: string }>(endpoints.changeUser, { igg: user.value }).subscribe({
            next: (response) => {
                // Clear session after successful API call but before redirect
                this.sessionService.clearSession();
                
                // Redirect to the URL provided by the backend
                // This will handle the new OAuth flow with _et parameter
                window.location.href = response.redirectUrl;
            },
            error: (error: any) => {
                console.error('Error changing user:', error);
                // Don't clear session on error, keep current user state
            },
        });
    }

    getRoleLabel(role: UserRole): string {
        const roleOption = ROLE_OPTIONS.find(option => option.value === role);
        return roleOption ? roleOption.label : '';
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
